import L from "leaflet";
import cameras from "./data/cameras.json";

const STATE_KEY = "hydrotools:coastal-webcams:v2";

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const x = raw ? JSON.parse(raw) : {};
    return {
      region: String(x.region || "all"),
      query: String(x.query || ""),
      pinnedOnly: x.pinnedOnly === true,
      pinnedIds: Array.isArray(x.pinnedIds) ? x.pinnedIds.map((v) => String(v)) : [],
      selectedId: String(x.selectedId || ""),
    };
  } catch {
    return {
      region: "all",
      query: "",
      pinnedOnly: false,
      pinnedIds: [],
      selectedId: "",
    };
  }
}

function saveState(s) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(s));
  } catch {}
}

function norm(s) {
  return String(s || "").trim().toLowerCase();
}

export const coastalWebcamsTool = {
  id: "coastal-webcams",
  name: "Coastal Webcams",
  init(app) {
    this._rt = this._rt ?? createRuntime(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};

function createRuntime(app) {
  const state = readState();
  let containerEl = null;
  const markerLayer = L.layerGroup();
  const markerById = new Map();
  const pinned = new Set(state.pinnedIds);
  let selectedId = state.selectedId || "";
  let status = "Ready.";
  let previewTick = 0;

  function listRegions() {
    const set = new Set(cameras.map((c) => String(c.region || "Other")));
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }

  function persist() {
    saveState({
      ...state,
      pinnedIds: Array.from(pinned),
      selectedId,
    });
  }

  function filteredRows() {
    const q = norm(state.query);
    return cameras.filter((c) => {
      if (state.region !== "all" && String(c.region || "") !== state.region) return false;
      if (state.pinnedOnly && !pinned.has(String(c.id))) return false;
      if (!q) return true;
      const hay = `${c.name || ""} ${c.region || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }

  function selectedCamera() {
    if (!selectedId) return null;
    return cameras.find((c) => String(c.id) === String(selectedId)) || null;
  }

  function markerStyle(camId) {
    const id = String(camId);
    if (id === String(selectedId)) {
      return { stroke: "#0f172a", fill: "#f59e0b", radius: 7, weight: 1.6 };
    }
    if (pinned.has(id)) {
      return { stroke: "#4c1d95", fill: "#c084fc", radius: 6, weight: 1.3 };
    }
    return { stroke: "#0e7490", fill: "#22d3ee", radius: 5.5, weight: 1.2 };
  }

  function redrawMarkers() {
    markerLayer.clearLayers();
    markerById.clear();
    const rows = filteredRows();
    rows.forEach((cam) => {
      const s = markerStyle(cam.id);
      const m = L.circleMarker([cam.lat, cam.lon], {
        radius: s.radius,
        color: s.stroke,
        weight: s.weight,
        fillColor: s.fill,
        fillOpacity: 0.95,
      })
        .bindTooltip(`${cam.name} (${cam.region})`)
        .on("click", () => {
          selectedId = String(cam.id);
          status = `Selected: ${cam.name}`;
          persist();
          redrawMarkers();
          render();
        })
        .addTo(markerLayer);
      markerById.set(String(cam.id), m);
    });
  }

  function zoomToSelected() {
    const cam = selectedCamera();
    if (!cam) return;
    app.map.instance.setView([cam.lat, cam.lon], Math.max(8, app.map.instance.getZoom()));
  }

  function previewHtml(cam) {
    if (!cam) return `<div class="small">Select a camera to preview.</div>`;
    if (cam.kind === "embed" && cam.embedUrl) {
      return `
        <div style="aspect-ratio:16/9; width:100%; border:1px solid rgba(148,163,184,.25); border-radius:8px; overflow:hidden; background:#020617;">
          <iframe
            src="${esc(cam.embedUrl)}"
            title="${esc(cam.name)}"
            style="width:100%; height:100%; border:0;"
            allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
            referrerpolicy="no-referrer-when-downgrade"
            allowfullscreen
          ></iframe>
        </div>
      `;
    }
    if (cam.kind === "image" && cam.imageUrl) {
      const src = `${cam.imageUrl}${String(cam.imageUrl).includes("?") ? "&" : "?"}t=${Date.now()}_${previewTick}`;
      return `
        <div style="aspect-ratio:16/9; width:100%; border:1px solid rgba(148,163,184,.25); border-radius:8px; overflow:hidden; background:#020617; display:flex; align-items:center; justify-content:center;">
          <img
            src="${esc(src)}"
            alt="${esc(cam.name)}"
            style="width:100%; height:100%; object-fit:contain; background:#020617;"
            loading="lazy"
            referrerpolicy="no-referrer"
          />
        </div>
      `;
    }
    return `<div class="small">Preview unavailable for this camera. Use Open Source.</div>`;
  }

  function togglePin(id) {
    const key = String(id);
    if (pinned.has(key)) pinned.delete(key);
    else pinned.add(key);
    persist();
    redrawMarkers();
    render();
  }

  function render() {
    if (!containerEl) return;
    const regions = listRegions();
    const rows = filteredRows();
    const cam = selectedCamera();
    const pinnedRows = cameras.filter((c) => pinned.has(String(c.id)));
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Coastal Webcams</div>
        <div class="small" style="margin-top:6px;">Browse curated coastal cameras, preview feeds, and pin favorites.</div>

        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <label class="small" for="cw-region">Region</label>
          <select id="cw-region">
            ${regions.map((r) => `<option value="${esc(r)}" ${state.region === r ? "selected" : ""}>${esc(r === "all" ? "All Regions" : r)}</option>`).join("")}
          </select>
        </div>

        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <label class="small" for="cw-query">Search</label>
          <input id="cw-query" type="text" value="${esc(state.query)}" placeholder="Name or region" />
        </div>

        <div class="row lm-add-row" style="margin-top:8px; align-items:center; gap:8px;">
          <input id="cw-pinned-only" type="checkbox" ${state.pinnedOnly ? "checked" : ""} />
          <label class="small" for="cw-pinned-only">Pinned only</label>
          <button id="cw-clear-pins" class="btn" ${pinned.size ? "" : "disabled"}>Clear Pins</button>
        </div>

        <div class="small" style="margin-top:8px;"><strong>Status:</strong> ${esc(status)}</div>
      </div>

      <details class="card lt-collapsible" open style="margin-top:8px; padding:8px 10px;">
        <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">
          Preview ${cam ? `- ${esc(cam.name)}` : ""}
        </summary>
        <div style="margin-top:8px;">
          ${previewHtml(cam)}
        </div>
        ${cam ? `
          <div class="row" style="margin-top:8px; gap:6px;">
            <button class="btn" id="cw-pin-selected">${pinned.has(String(cam.id)) ? "Unpin" : "Pin"}</button>
            ${cam.kind === "image" ? `<button class="btn" id="cw-refresh-preview">Refresh Frame</button>` : ""}
            <button class="btn" id="cw-zoom-selected">Zoom To</button>
            <a class="btn" href="${esc(cam.sourceUrl || "#")}" target="_blank" rel="noopener noreferrer">Open Source</a>
          </div>
          <div class="small" style="margin-top:6px;">Region: ${esc(cam.region || "Unknown")}</div>
        ` : ""}
      </details>

      <details class="card lt-collapsible" ${pinnedRows.length ? "open" : ""} style="margin-top:8px; padding:8px 10px;">
        <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">Watchlist (${pinnedRows.length})</summary>
        <div style="margin-top:8px; display:grid; gap:6px;">
          ${pinnedRows.length ? pinnedRows.map((c) => `
            <div class="row" style="align-items:center; gap:6px; border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;">
              <button class="btn cw-select" data-id="${esc(c.id)}">${esc(c.name)}</button>
              <span class="small" style="margin-left:auto;">${esc(c.region)}</span>
              <button class="btn cw-pin-toggle" data-id="${esc(c.id)}">x</button>
            </div>
          `).join("") : `<div class="small">No pinned cameras.</div>`}
        </div>
      </details>

      <details class="card lt-collapsible" open style="margin-top:8px; padding:8px 10px;">
        <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">Camera List (${rows.length})</summary>
        <div style="margin-top:8px; display:grid; gap:6px; max-height:280px; overflow:auto;">
          ${rows.length ? rows.map((c) => `
            <div class="row" style="align-items:center; gap:6px; border:1px solid ${String(c.id) === String(selectedId) ? "rgba(59,130,246,.8)" : "rgba(148,163,184,.2)"}; border-radius:8px; padding:6px;">
              <button class="btn cw-select" data-id="${esc(c.id)}">${esc(c.name)}</button>
              <span class="small" style="margin-left:auto;">${esc(c.region)}</span>
              <button class="btn cw-pin-toggle" data-id="${esc(c.id)}">${pinned.has(String(c.id)) ? "Unpin" : "Pin"}</button>
            </div>
          `).join("") : `<div class="small">No cameras match current filters.</div>`}
        </div>
      </details>
    `;

    const regionEl = containerEl.querySelector("#cw-region");
    const queryEl = containerEl.querySelector("#cw-query");
    const pinnedOnlyEl = containerEl.querySelector("#cw-pinned-only");
    const clearPinsBtn = containerEl.querySelector("#cw-clear-pins");
    regionEl?.addEventListener("change", () => {
      state.region = String(regionEl.value || "all");
      persist();
      redrawMarkers();
      render();
    });
    queryEl?.addEventListener("input", () => {
      state.query = String(queryEl.value || "");
      persist();
      redrawMarkers();
      render();
    });
    pinnedOnlyEl?.addEventListener("change", () => {
      state.pinnedOnly = !!pinnedOnlyEl.checked;
      persist();
      redrawMarkers();
      render();
    });
    clearPinsBtn?.addEventListener("click", () => {
      pinned.clear();
      persist();
      redrawMarkers();
      render();
    });

    containerEl.querySelectorAll(".cw-select").forEach((el) => {
      el.addEventListener("click", () => {
        const id = String(el.getAttribute("data-id") || "");
        if (!id) return;
        selectedId = id;
        const c = cameras.find((x) => String(x.id) === id);
        status = c ? `Selected: ${c.name}` : "Camera selected.";
        persist();
        redrawMarkers();
        render();
      });
    });
    containerEl.querySelectorAll(".cw-pin-toggle").forEach((el) => {
      el.addEventListener("click", () => {
        const id = String(el.getAttribute("data-id") || "");
        if (!id) return;
        togglePin(id);
      });
    });
    containerEl.querySelector("#cw-pin-selected")?.addEventListener("click", () => {
      if (!cam) return;
      togglePin(cam.id);
    });
    containerEl.querySelector("#cw-refresh-preview")?.addEventListener("click", () => {
      previewTick += 1;
      status = "Preview refreshed.";
      render();
    });
    containerEl.querySelector("#cw-zoom-selected")?.addEventListener("click", () => {
      zoomToSelected();
    });
  }

  function mount(nextContainer) {
    containerEl = nextContainer;
    app.map.instance.addLayer(markerLayer);
    if (!selectedId && cameras.length) selectedId = String(cameras[0].id);
    persist();
    redrawMarkers();
    render();
  }

  function destroy() {
    app.map.instance.removeLayer(markerLayer);
    markerLayer.clearLayers();
    markerById.clear();
  }

  return { mount, destroy };
}
